import { Button, Form, FormGroup, Nav, Tab } from "react-bootstrap";
import { Help, HelpItem, HelpItemTranslation, HelpLanguage, emptyHelpItem, emptyHelpItemTranslation } from "../../interfaces/Help";
import { LANGUAGES } from "../../../utils/data/Languages";
import { useState } from "react";
import CustomEditor from "../../elements/CustomEditor";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Overlay } from "../../elements/Overlay";
import { handleApiError } from "../../../utils/api";

export interface HelpEditProps {
    help: Help;
    submitCallback: CallableFunction;
    closeCallback: CallableFunction;
}

export default function HelpEdit(props: HelpEditProps) {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const [helpItems, setHelpItems] = useState<HelpItem[]>(props.help.items);
    const [showAddHelpItemForm, setShowAddHelpItemForm] = useState(false);
    const [showEditHelpItemForm, setShowEditHelpItemForm] = useState(false);
    const [showDeleteHelpItemForm, setShowDeleteHelpItemForm] = useState(false);
    const [helpItem, setHelpItem] = useState<HelpItem>(emptyHelpItem);
    const [helpItemIndex, setHelpItemIndex] = useState(-1);

    // State for translations by language
    const [currentLang, setCurrentLang] = useState<HelpLanguage>('en');
    const [translations, setTranslations] = useState<{ [key in HelpLanguage]?: HelpItemTranslation }>({
        en: { ...emptyHelpItemTranslation, lang: 'en' }
    });

    // State for translation loading
    const [translatingTitle, setTranslatingTitle] = useState(false);
    const [translatingTeaser, setTranslatingTeaser] = useState(false);
    const [translatingBody, setTranslatingBody] = useState(false);
    const [translatingExamples, setTranslatingExamples] = useState(false);
    const [translatingReferences, setTranslatingReferences] = useState(false);

    const handleClose = () => {
        props.closeCallback();
    }

    const handleSubmit = (event: any) => {
        event.preventDefault();
        let error = false;
        let error_messages: string[] = [];
        if (error) {
            alert(error_messages.join('\n'));
        } else {
            const newHelp = Object.assign({}, props.help);
            newHelp.name = event.target.name.value;
            newHelp.items = helpItems.map((hi: HelpItem) => { return hi; });
            newHelp.weight = parseInt(event.target.weight.value) || 0;
            const requestOptionsPost = {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify(newHelp)
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/help/' + props.help._id, requestOptionsPost)
                .then(async response => {
                    if (handleApiError(response, navigate)) {
                        return null;
                    }

                    // API returns just the ID as plain text, not JSON
                    const text = await response.text();
                    return { id: text.trim() };
                })
                .then(data => {
                    if (!data) return;
                    props.submitCallback(newHelp);
                });
        }
    }

    const handleShowEditHelpItemForm = (event: any, index: number) => {
        event.preventDefault();
        setHelpItemIndex(index);
        const editItem = helpItems[index];
        setHelpItem(editItem);

        // Load translations into state
        const translationsMap: { [key in HelpLanguage]?: HelpItemTranslation } = {};
        editItem.translations.forEach(trans => {
            translationsMap[trans.lang] = { ...trans };
        });
        setTranslations(translationsMap);
        setCurrentLang(editItem.defaultLang);
        setShowEditHelpItemForm(true);
    }

    const handleShowDeleteHelpItemForm = (event: any, index: number) => {
        event.preventDefault();
        setHelpItemIndex(index);
        const newHelpItem = Object.assign({}, helpItems[index]);
        setHelpItem(newHelpItem);
        setShowDeleteHelpItemForm(true);
    }

    const handleShowAddHelpItemForm = () => {
        const newHelpItem = Object.assign({}, emptyHelpItem);
        setHelpItem(newHelpItem);
        setTranslations({
            en: { ...emptyHelpItemTranslation, lang: 'en' }
        });
        setCurrentLang('en');
        setShowAddHelpItemForm(true);
    }

    const handleHideAddHelpItemForm = () => {
        setShowAddHelpItemForm(false);
    }

    const handleAddHelpItemSubmit = (event: any) => {
        event.preventDefault();

        // Create array of translations from the translations state
        const translationArray: HelpItemTranslation[] = Object.values(translations).filter(t => t !== undefined) as HelpItemTranslation[];

        const newHelpItem: HelpItem = {
            _id: '',
            defaultLang: 'en', // Always English
            translations: translationArray
        };

        const newHelpItems = [...helpItems, newHelpItem];
        setHelpItems(newHelpItems);
        setShowAddHelpItemForm(false);
    }

    const handleEditHelpItemSubmit = (event: any) => {
        event.preventDefault();

        // Create array of translations from the translations state
        const translationArray: HelpItemTranslation[] = Object.values(translations).filter(t => t !== undefined) as HelpItemTranslation[];

        const updatedHelpItem: HelpItem = {
            ...helpItem,
            defaultLang: 'en', // Always English
            translations: translationArray
        };

        const newHelpItems = helpItems.map((hi: HelpItem, hiIndex: number) => {
            if (hiIndex === helpItemIndex) {
                return updatedHelpItem;
            } else {
                return hi;
            }
        });
        setHelpItems(newHelpItems);
        setShowEditHelpItemForm(false);
    }

    const handleHideEditHelpItemForm = () => {
        setShowEditHelpItemForm(false);
    }

    const handleDeleteHelpItemSubmit = (event: any) => {
        event.preventDefault();
        const newHelpItems = helpItems.filter((hi: HelpItem, index: number) => index !== helpItemIndex);
        setHelpItems(newHelpItems);
        setShowDeleteHelpItemForm(false);
    }

    const handleHideDeleteHelpItemForm = () => {
        setShowDeleteHelpItemForm(false);
    }

    const updateTranslationField = (lang: HelpLanguage, field: keyof HelpItemTranslation, value: string) => {
        setTranslations(prev => ({
            ...prev,
            [lang]: {
                ...(prev[lang] || { ...emptyHelpItemTranslation, lang }),
                [field]: value
            }
        }));
    }

    const handleLanguageTabChange = (lang: string | null) => {
        if (lang) {
            setCurrentLang(lang as HelpLanguage);
            // Ensure translation exists for this language
            if (!translations[lang as HelpLanguage]) {
                setTranslations(prev => ({
                    ...prev,
                    [lang]: { ...emptyHelpItemTranslation, lang: lang as HelpLanguage }
                }));
            }
        }
    }

    const getHelpItemTitle = (item: HelpItem): string => {
        const defaultTranslation = item.translations.find(t => t.lang === item.defaultLang);
        return defaultTranslation?.title || item.translations[0]?.title || t("Help item");
    }

    const translateWithDeepL = async (text: string, targetLang: HelpLanguage, field: keyof HelpItemTranslation, setLoading: (loading: boolean) => void) => {
        if (!text.trim()) {
            alert(t("Please enter text to translate"));
            return;
        }

        if (targetLang === 'en') {
            alert(t("Cannot translate to English (source language)"));
            return;
        }

        if (targetLang === 'ca') {
            alert(t("DeepL does not support Catalan"));
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(import.meta.env.VITE_API_URL + '/v1/translate', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token'),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text: text,
                    targetLang: targetLang,
                    sourceLang: 'en'
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Translation failed');
            }

            const result = await response.json();
            updateTranslationField(targetLang, field, result.translatedText);

        } catch (error: any) {
            console.error('Translation error:', error);
            alert(t("Translation error: ") + error.message);
        } finally {
            setLoading(false);
        }
    }

    const handleTranslateTitle = () => {
        const englishTranslation = translations['en'];
        if (englishTranslation && englishTranslation.title) {
            translateWithDeepL(englishTranslation.title, currentLang, 'title', setTranslatingTitle);
        } else {
            alert(t("Please enter English title first"));
        }
    }

    const handleTranslateTeaser = () => {
        const englishTranslation = translations['en'];
        if (englishTranslation && englishTranslation.teaser) {
            translateWithDeepL(englishTranslation.teaser, currentLang, 'teaser', setTranslatingTeaser);
        } else {
            alert(t("Please enter English teaser first"));
        }
    }

    const handleTranslateBody = () => {
        const englishTranslation = translations['en'];
        if (englishTranslation && englishTranslation.body) {
            translateWithDeepL(englishTranslation.body, currentLang, 'body', setTranslatingBody);
        } else {
            alert(t("Please enter English body first"));
        }
    }

    const handleTranslateExamples = () => {
        const englishTranslation = translations['en'];
        if (englishTranslation && englishTranslation.examples) {
            translateWithDeepL(englishTranslation.examples, currentLang, 'examples', setTranslatingExamples);
        } else {
            alert(t("Please enter English examples first"));
        }
    }

    const handleTranslateReferences = () => {
        const englishTranslation = translations['en'];
        if (englishTranslation && englishTranslation.references) {
            translateWithDeepL(englishTranslation.references, currentLang, 'references', setTranslatingReferences);
        } else {
            alert(t("Please enter English references first"));
        }
    }

    const doNothing = () => {

    }

    return(
        <Overlay title={t("Edit help")} onClose={handleClose} className="admin-edit-help-container">
            <div className={ 'help-item-form-container' + ((showAddHelpItemForm || showEditHelpItemForm || showDeleteHelpItemForm) ? ' hide' : '') }>
                            <Form onSubmit={ handleSubmit }>
                                <FormGroup className="name" controlId="name">
                                    <Form.Label aria-required={ true }>{ t("Name") }</Form.Label>
                                    <Form.Control type="text" required={ true } defaultValue={ props.help.name }
                                        placeholder={ t("Enter a descriptive name") } />
                                </FormGroup>
                                <FormGroup className="weight" controlId="weight">
                                    <Form.Label>{ t("Weight") }</Form.Label>
                                    <Form.Control type="number" defaultValue={ props.help.weight } />
                                    <Form.Text id="weightHelpBlock" muted>
                                        { t("Enter the position of this help content relative to other help contents. The higher the value the lower it will be sorted.") }
                                    </Form.Text>
                                </FormGroup>
                                <div className="help-items">
                                    <strong>{ t("Help items") }</strong>
                                    { helpItems.length === 0 && !showAddHelpItemForm && !showEditHelpItemForm && !showDeleteHelpItemForm &&
                                        <div className="no-items-message">
                                            { t("There are no help items defined. Add your first help item.") }
                                        </div>
                                    }
                                    { helpItems.length > 0 && !showAddHelpItemForm && !showEditHelpItemForm && !showDeleteHelpItemForm &&
                                        <div className="help-items-list">
                                            { helpItems.map((hi: HelpItem, index: number) => {
                                                return(
                                                    <div className="help-item-elem" key={index}>
                                                        <div className="title">
                                                            { getHelpItemTitle(hi) }
                                                            <span className="language-badge">({ hi.translations.map(t => t.lang.toUpperCase()).join(', ') })</span>
                                                        </div>
                                                        <div className="help-item-actions">
                                                            <a className="edit" title={ t("Edit help") }
                                                                href="#" onClick={ (event) => handleShowEditHelpItemForm(event, index) }></a>
                                                            <a className="delete" title={ t("Delete help") }
                                                                href="#" onClick={ (event) => handleShowDeleteHelpItemForm(event, index) }></a>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    }
                                    <div className="help-item-actions">
                                        <Button variant="secondary" onClick={ handleShowAddHelpItemForm }>
                                            { t("Add help item") }
                                        </Button>
                                    </div>
                                </div>
                                <div className="actions">
                                    <Button type="submit" variant="primary">{ t("Save") }</Button>
                                </div>
                            </Form>
                        </div>
                        { showAddHelpItemForm &&
                            <Form onSubmit={ handleAddHelpItemSubmit } className="add-help-item">
                                <h5>{ t("Add help Item") }</h5>

                                <Tab.Container activeKey={currentLang} onSelect={handleLanguageTabChange}>
                                    <Nav variant="tabs">
                                        {LANGUAGES.map(lang => (
                                            <Nav.Item key={lang.code}>
                                                <Nav.Link eventKey={lang.code}>{lang.name}</Nav.Link>
                                            </Nav.Item>
                                        ))}
                                    </Nav>
                                    <Tab.Content style={{ marginTop: '20px' }}>
                                        {LANGUAGES.map(lang => {
                                            const trans = translations[lang.code] || { ...emptyHelpItemTranslation, lang: lang.code };
                                            return (
                                                <Tab.Pane key={lang.code} eventKey={lang.code}>
                                                    <FormGroup className="title" controlId={`title-${lang.code}`}>
                                                        <Form.Label>{ t("Title") }</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={trans.title}
                                                            onChange={(e) => updateTranslationField(lang.code, 'title', e.target.value)}
                                                            placeholder={ t("Enter the title") } />
                                                    </FormGroup>
                                                    <div className="field field-teaser">
                                                        <CustomEditor
                                                            change={(data: string) => updateTranslationField(lang.code, 'teaser', data)}
                                                            changeFormatted={ doNothing }
                                                            data={ trans.teaser }
                                                            formatted={ '' }
                                                            id={`teaser-${lang.code}`}
                                                            label={ t("Teaser") }
                                                            npl={ false }
                                                            task_id={ '' }
                                                        />
                                                    </div>
                                                    <div className="field field-body">
                                                        <CustomEditor
                                                            change={(data: string) => updateTranslationField(lang.code, 'body', data)}
                                                            changeFormatted={ doNothing }
                                                            data={ trans.body }
                                                            formatted={ '' }
                                                            id={`body-${lang.code}`}
                                                            label={ t("Body")}
                                                            npl={ false }
                                                            task_id={ '' }
                                                        />
                                                    </div>
                                                    <div className="field field-examples">
                                                        <CustomEditor
                                                            change={(data: string) => updateTranslationField(lang.code, 'examples', data)}
                                                            changeFormatted={ doNothing }
                                                            data={ trans.examples }
                                                            formatted={ '' }
                                                            id={`examples-${lang.code}`}
                                                            label={ t("Examples")}
                                                            npl={ false }
                                                            task_id={ '' }
                                                        />
                                                    </div>
                                                    <div className="field field-references">
                                                        <CustomEditor
                                                            change={(data: string) => updateTranslationField(lang.code, 'references', data)}
                                                            changeFormatted={ doNothing }
                                                            data={ trans.references }
                                                            formatted={ '' }
                                                            id={`references-${lang.code}`}
                                                            label={ t("References")}
                                                            npl={ false }
                                                            task_id={ '' }
                                                        />
                                                    </div>
                                                </Tab.Pane>
                                            );
                                        })}
                                    </Tab.Content>
                                </Tab.Container>
                                <div className="actions">
                                    <Button type="submit" variant="primary">{ t("Save") }</Button>
                                    <Button onClick={ handleHideAddHelpItemForm } variant="warning">{ t("Cancel") }</Button>
                                </div>
                            </Form>
                        }
                        { showEditHelpItemForm &&
                            <Form onSubmit={ handleEditHelpItemSubmit } className="edit-help-item">
                                <h5>{ t("Edit Help Item") }</h5>

                                <Tab.Container activeKey={currentLang} onSelect={handleLanguageTabChange}>
                                    <Nav variant="tabs">
                                        {LANGUAGES.map(lang => (
                                            <Nav.Item key={lang.code}>
                                                <Nav.Link eventKey={lang.code}>{lang.name}</Nav.Link>
                                            </Nav.Item>
                                        ))}
                                    </Nav>
                                    <Tab.Content style={{ marginTop: '20px' }}>
                                        {LANGUAGES.map(lang => {
                                            const trans = translations[lang.code] || { ...emptyHelpItemTranslation, lang: lang.code };
                                            const showTranslateLink = lang.code !== 'en' && lang.code !== 'ca';
                                            return (
                                                <Tab.Pane key={lang.code} eventKey={lang.code}>
                                                    <FormGroup className="title" controlId={`title-${lang.code}`}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Form.Label>{ t("Title") }</Form.Label>
                                                            {showTranslateLink && (
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => { e.preventDefault(); handleTranslateTitle(); }}
                                                                    style={{ fontSize: '0.9em', marginLeft: '10px' }}
                                                                >
                                                                    {translatingTitle ? t("Translating...") : t("Translate with DeepL")}
                                                                </a>
                                                            )}
                                                        </div>
                                                        <Form.Control
                                                            type="text"
                                                            value={trans.title}
                                                            onChange={(e) => updateTranslationField(lang.code, 'title', e.target.value)}
                                                            placeholder={ t("Enter the title") }
                                                            disabled={translatingTitle} />
                                                    </FormGroup>
                                                    <div className="field field-teaser">
                                                        {showTranslateLink && (
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '5px' }}>
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => { e.preventDefault(); handleTranslateTeaser(); }}
                                                                    style={{ fontSize: '0.9em' }}
                                                                >
                                                                    {translatingTeaser ? t("Translating...") : t("Translate with DeepL")}
                                                                </a>
                                                            </div>
                                                        )}
                                                        <CustomEditor
                                                            change={(data: string) => updateTranslationField(lang.code, 'teaser', data)}
                                                            changeFormatted={ doNothing }
                                                            data={ trans.teaser }
                                                            formatted={ '' }
                                                            id={`teaser-${lang.code}`}
                                                            label={ t("Teaser") }
                                                            npl={ false }
                                                            task_id={ '' }
                                                        />
                                                    </div>
                                                    <div className="field field-body">
                                                        {showTranslateLink && (
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '5px' }}>
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => { e.preventDefault(); handleTranslateBody(); }}
                                                                    style={{ fontSize: '0.9em' }}
                                                                >
                                                                    {translatingBody ? t("Translating...") : t("Translate with DeepL")}
                                                                </a>
                                                            </div>
                                                        )}
                                                        <CustomEditor
                                                            change={(data: string) => updateTranslationField(lang.code, 'body', data)}
                                                            changeFormatted={ doNothing }
                                                            data={ trans.body }
                                                            formatted={ '' }
                                                            id={`body-${lang.code}`}
                                                            label={ t("Body")}
                                                            npl={ false }
                                                            task_id={ '' }
                                                        />
                                                    </div>
                                                    <div className="field field-examples">
                                                        {showTranslateLink && (
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '5px' }}>
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => { e.preventDefault(); handleTranslateExamples(); }}
                                                                    style={{ fontSize: '0.9em' }}
                                                                >
                                                                    {translatingExamples ? t("Translating...") : t("Translate with DeepL")}
                                                                </a>
                                                            </div>
                                                        )}
                                                        <CustomEditor
                                                            change={(data: string) => updateTranslationField(lang.code, 'examples', data)}
                                                            changeFormatted={ doNothing }
                                                            data={ trans.examples }
                                                            formatted={ '' }
                                                            id={`examples-${lang.code}`}
                                                            label={ t("Examples")}
                                                            npl={ false }
                                                            task_id={ '' }
                                                        />
                                                    </div>
                                                    <div className="field field-references">
                                                        {showTranslateLink && (
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '5px' }}>
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => { e.preventDefault(); handleTranslateReferences(); }}
                                                                    style={{ fontSize: '0.9em' }}
                                                                >
                                                                    {translatingReferences ? t("Translating...") : t("Translate with DeepL")}
                                                                </a>
                                                            </div>
                                                        )}
                                                        <CustomEditor
                                                            change={(data: string) => updateTranslationField(lang.code, 'references', data)}
                                                            changeFormatted={ doNothing }
                                                            data={ trans.references }
                                                            formatted={ '' }
                                                            id={`references-${lang.code}`}
                                                            label={ t("References")}
                                                            npl={ false }
                                                            task_id={ '' }
                                                        />
                                                    </div>
                                                </Tab.Pane>
                                            );
                                        })}
                                    </Tab.Content>
                                </Tab.Container>
                                <div className="actions">
                                    <Button type="submit" variant="primary">{ t("Modify") }</Button>
                                    <Button onClick={ handleHideEditHelpItemForm } variant="warning">{ t("Cancel") }</Button>
                                </div>
                            </Form>
                        }
                        { showDeleteHelpItemForm &&
                            <Form onSubmit={ handleDeleteHelpItemSubmit } className="delete-help-item">
                                <h5>{ t('Delete help item') }</h5>
                                <FormGroup className="confirmation-message" controlId="confirmation">
                                    <Form.Text id="confirmationMessage">
                                        { t("Are you sure you want to delete Help Item named?") } <strong>{ getHelpItemTitle(helpItem) }</strong>? { t("This action cannot be undone.") }
                                    </Form.Text>
                                </FormGroup>
                                <div className="actions">
                                    <Button type="submit" variant="primary">{ t("Delete") }</Button>
                                    <Button onClick={ handleHideDeleteHelpItemForm } variant="warning">{ t("Cancel") }</Button>
                                </div>
                            </Form>
                        }
        </Overlay>
    );
}
